import React, { FC, memo, useCallback, useEffect } from "react";
import { Dialog } from "@mui/material";
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";
import { MdClose } from "react-icons/md";

interface ImageLightboxProps {
    images: string[];
    index: number;
    open: boolean;
    onClose: () => void;
    onIndexChange: (index: number) => void;
}

// Full-screen image viewer (the mobile app's full screen image modal): large image, arrows, counter, close.
const ImageLightbox: FC<ImageLightboxProps> = ({ images, index, open, onClose, onIndexChange }) => {
    const count = images.length;
    const prev = useCallback(() => onIndexChange((index - 1 + count) % count), [index, count, onIndexChange]);
    const next = useCallback(() => onIndexChange((index + 1) % count), [index, count, onIndexChange]);

    useEffect(() => {
        if (!open) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "ArrowLeft") prev();
            if (e.key === "ArrowRight") next();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open, prev, next]);

    if (!count) return null;

    return (
        <Dialog open={open} onClose={onClose} fullScreen PaperProps={{ className: "pb-lightbox" }}>
            <button type="button" className="pb-lightbox-btn pb-lightbox-close" aria-label="Close" onClick={onClose}>
                <MdClose size={22} />
            </button>

            <div className="pb-lightbox-stage" onClick={onClose}>
                <img src={images[index]} alt="" onClick={(e) => e.stopPropagation()} />
            </div>

            {count > 1 && (
                <>
                    <button type="button" className="pb-lightbox-btn pb-lightbox-prev" aria-label="Previous image" onClick={prev}>
                        <IoIosArrowBack size={24} />
                    </button>
                    <button type="button" className="pb-lightbox-btn pb-lightbox-next" aria-label="Next image" onClick={next}>
                        <IoIosArrowForward size={24} />
                    </button>
                    <div className="pb-lightbox-counter">{index + 1} / {count}</div>
                </>
            )}
        </Dialog>
    );
};

export default memo(ImageLightbox);
